// FormBuilderMediaSection — the "Media & Notes" body inside Advanced options:
// flat rows of icon · label · ⓘ (native title tooltip) · Switch, with indented
// sub-blocks under Allow pictures (required / upload checkboxes · NUMBER OF
// PICTURES min/max), Allow videos (upload · DURATION LIMIT min/sec), Watermark
// (text · top/bottom alignment · center) and Restrict camera orientation (a
// vertical/horizontal Select). Watermark is its OWN top-level row, not a
// sub-property of pictures/videos (legacy contract, WatermarkComponent.js):
// its switch is disabled until pictures or videos are allowed, and turning the
// last of those off — or watermark itself off — resets the watermark set in
// the same patch. The watermark text is a MentionTextarea (the OTP idiom):
// `#` inserts User Name / Date And Time or a form field, stored as #[Display]#
// tokens with watermarkUsedFieldsMap = {display: wireId} riding every patch.

import { Trans, useTranslation } from 'react-i18next';
import {
  Checkbox,
  Icon,
  Input,
  MentionTextarea,
  SegmentedControl,
  SelectDropdown,
  Switch,
  type MentionOption,
} from '@/components/common';
import type { ElementProperties } from './utils/formElementDefaults';
import type { BuilderField, BuilderPage } from './utils/builderModel';

/** Top-level toggle rows in design order. */
const ROWS: { key: string; icon: string }[] = [
  { key: 'allowNotes', icon: 'notebook-pen' },
  { key: 'allowPictures', icon: 'image' },
  { key: 'allowVideos', icon: 'video' },
  { key: 'enableWatermark', icon: 'droplets' },
  { key: 'allowDocuments', icon: 'file-text' },
  { key: 'showTimeStamp', icon: 'clock' },
  { key: 'showTitle', icon: 'heading' },
  { key: 'restrictCameraOrientation', icon: 'smartphone' },
  { key: 'allowDownload', icon: 'hard-drive-download' },
];

/** System mentions for the watermark text. `display` is deliberately a literal,
 *  not a t() lookup: it is embedded in the stored watermarkText and doubles as
 *  the watermarkUsedFieldsMap key, so translating it would desync stored data
 *  across authoring locales (the OTP_MENTIONS rule). */
const WATERMARK_MENTIONS: MentionOption[] = [
  { value: 'user-name', display: 'User Name', icon: 'user' },
  { value: 'date-time', display: 'Date And Time', icon: 'calendar-clock' },
];

interface FormBuilderMediaSectionProps {
  field: BuilderField | BuilderPage;
  onChange: (properties: ElementProperties) => void;
  /** Form-field mention options (whole-form, titled fields only). */
  watermarkMentions?: MentionOption[];
}

const FormBuilderMediaSection = ({
  field,
  onChange,
  watermarkMentions,
}: FormBuilderMediaSectionProps) => {
  const { t } = useTranslation();

  const properties = field.properties;
  if (!('mediaProperties' in properties)) return null;
  const mp = properties.mediaProperties as Record<string, unknown>;

  const setMedia = (patch: Record<string, unknown>) =>
    onChange({
      ...properties,
      mediaProperties: { ...(properties.mediaProperties as object), ...patch },
    } as ElementProperties);

  // Number input for a min/max-style pair member.
  const numInput = (key: string, label: string) => (
    <Input
      size='sm'
      type='number'
      className='form-builder-props__med-num'
      value={String(mp[key] ?? '')}
      aria-label={label}
      onChange={(e) => setMedia({ [key]: Number(e.target.value) })}
    />
  );

  // Watermark defaults (formElementDefaults' mediaProperties) — restored
  // whenever the feature turns off, so nothing stale rides in the payload.
  const WATERMARK_OFF = {
    enableWatermark: false,
    watermarkText: '',
    watermarkAlignment: 'bottom',
    watermarkTextCenter: false,
    watermarkUsedFieldsMap: {} as Record<string, string>,
  };

  // One patch per toggle, ValidationSection's idiom: turning watermark off
  // resets its detail values; turning the LAST of pictures/videos off drags
  // watermark (and its details) off with it — the legacy cascade, and what
  // keeps the disabled row below from ever trapping an ON switch.
  const toggleRow = (key: string, on: boolean) => {
    if (key === 'enableWatermark' && !on) {
      setMedia(WATERMARK_OFF);
      return;
    }
    if ((key === 'allowPictures' || key === 'allowVideos') && !on) {
      const other = key === 'allowPictures' ? 'allowVideos' : 'allowPictures';
      if (mp[other] !== true && mp.enableWatermark === true) {
        setMedia({ [key]: false, ...WATERMARK_OFF });
        return;
      }
    }
    setMedia({ [key]: on });
  };

  // The watermark row only means something while some media can carry it. An
  // ON switch is never disabled (stale saved data must stay turn-off-able).
  const watermarkOn = mp.enableWatermark === true;
  const watermarkLocked = !watermarkOn && mp.allowPictures !== true && mp.allowVideos !== true;

  const watermarkDetails = (
    <div className='form-builder-props__med-wm'>
      <span className='form-builder-props__label'>
        {t('forms_builder_props_med_watermarkText')}
      </span>
      <MentionTextarea
        rows={3}
        value={(mp.watermarkText as string) ?? ''}
        mentionMap={(mp.watermarkUsedFieldsMap as Record<string, string>) ?? {}}
        options={[...WATERMARK_MENTIONS, ...(watermarkMentions ?? [])]}
        placeholder={t('forms_builder_props_med_watermarkText_ph')}
        aria-label={t('forms_builder_props_med_watermarkText')}
        onChangeValue={(next, map) =>
          setMedia({ watermarkText: next, watermarkUsedFieldsMap: map })
        }
      />
      <span className='form-builder-props__otp-hint'>
        <Trans i18nKey='forms_builder_props_med_watermark_hint'>
          Type <strong>#</strong> to insert a variable
        </Trans>
      </span>
      <span className='form-builder-props__label'>
        {t('forms_builder_props_med_watermarkAlignment')}
      </span>
      <SegmentedControl
        aria-label={t('forms_builder_props_med_watermarkAlignment')}
        value={String(mp.watermarkAlignment ?? 'bottom')}
        onChange={(v) => setMedia({ watermarkAlignment: v })}
        options={[
          { value: 'bottom', label: t('forms_builder_props_med_wa_bottom'), icon: 'panel-bottom' },
          { value: 'top', label: t('forms_builder_props_med_wa_top'), icon: 'panel-top' },
        ]}
      />
      <Checkbox
        label={t('forms_builder_props_med_watermarkTextCenter')}
        checked={mp.watermarkTextCenter === true}
        onChange={(e) => setMedia({ watermarkTextCenter: e.target.checked })}
        size='sm'
      />
    </div>
  );
  // Indented dependents per top-level toggle (rendered while it's on).
  const subBlock = (key: string) => {
    if (key === 'allowPictures') {
      return (
        <div className='form-builder-props__med-sub'>
          <Checkbox
            label={t('forms_builder_props_med_requiredPictures')}
            checked={mp.requiredPictures === true}
            onChange={(e) => setMedia({ requiredPictures: e.target.checked })}
            size='sm'
          />
          <Checkbox
            label={t('forms_builder_props_med_allowUpload')}
            checked={mp.allowPictureUpload === true}
            onChange={(e) => setMedia({ allowPictureUpload: e.target.checked })}
            size='sm'
          />
          <span className='form-builder-props__med-heading'>
            {t('forms_builder_props_med_pictures_count')}
          </span>
          <div className='form-builder-props__med-pair'>
            <span className='form-builder-props__med-unit'>{t('forms_builder_props_med_min')}</span>
            {numInput('minPictures', t('forms_builder_props_med_min'))}
            <span className='form-builder-props__med-unit'>{t('forms_builder_props_med_max')}</span>
            {numInput('maxPictures', t('forms_builder_props_med_max'))}
          </div>
        </div>
      );
    }
    if (key === 'allowVideos') {
      return (
        <div className='form-builder-props__med-sub'>
          <Checkbox
            label={t('forms_builder_props_med_allowUpload')}
            checked={mp.allowVideoUpload === true}
            onChange={(e) => setMedia({ allowVideoUpload: e.target.checked })}
            size='sm'
          />
          <span className='form-builder-props__med-heading'>
            {t('forms_builder_props_med_duration')}
          </span>
          <div className='form-builder-props__med-pair'>
            {numInput('videoMinutes', t('forms_builder_props_med_min_unit'))}
            <span className='form-builder-props__med-unit'>
              {t('forms_builder_props_med_min_unit')}
            </span>
            {numInput('videoSeconds', t('forms_builder_props_med_sec_unit'))}
            <span className='form-builder-props__med-unit'>
              {t('forms_builder_props_med_sec_unit')}
            </span>
          </div>
        </div>
      );
    }
    if (key === 'enableWatermark') {
      return <div className='form-builder-props__med-sub'>{watermarkDetails}</div>;
    }
    if (key === 'restrictCameraOrientation') {
      return (
        <div className='form-builder-props__med-sub'>
          {/* <string> explicitly: the inline option literals would otherwise
              narrow V to 'vertical' | 'horizontal', which the String()-coerced
              value (mediaProperties is untyped) is not assignable to. */}
          <SelectDropdown<string>
            size='sm'
            aria-label={t('forms_builder_props_med_restrictCameraOrientation')}
            value={String(mp.cameraOrientation ?? 'vertical')}
            options={[
              { value: 'vertical', label: t('forms_builder_props_med_co_vertical') },
              { value: 'horizontal', label: t('forms_builder_props_med_co_horizontal') },
            ]}
            onChange={(next) => setMedia({ cameraOrientation: next })}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className='form-builder-props__med'>
      {ROWS.map(({ key, icon }) => (
        <div key={key} className='form-builder-props__med-item'>
          <div className='form-builder-props__med-row'>
            <span className='form-builder-props__med-icon'>
              <Icon name={icon} size={16} />
            </span>
            <span className='form-builder-props__med-label'>
              {t(`forms_builder_props_med_${key}`)}
            </span>
            <span
              className='form-builder-props__med-info'
              title={t(`forms_builder_props_med_${key}_desc`)}
            >
              <Icon name='info' size={13} />
            </span>
            <Switch
              checked={mp[key] === true}
              disabled={key === 'enableWatermark' && watermarkLocked}
              aria-label={t(`forms_builder_props_med_${key}`)}
              // Name the culprit, as the validation section does.
              title={
                key === 'enableWatermark' && watermarkLocked
                  ? t('forms_builder_props_med_enableWatermark_requires')
                  : undefined
              }
              onChange={(e) => toggleRow(key, e.target.checked)}
              size='sm'
            />
          </div>
          {mp[key] === true && subBlock(key)}
        </div>
      ))}
    </div>
  );
};

export default FormBuilderMediaSection;
